import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { useLiveMarkets } from "@/hooks/useLiveMarkets";
import { cryptoDataService } from "@/services/cryptoDataService";
import MarketStatsBanner from "@/components/markets/MarketStatsBanner";
import MarketPulsePanel from "@/components/markets/MarketPulsePanel";

import HighlightsBento from "@/components/markets/HighlightsBento";
import MarketsTable from "@/components/markets/MarketsTable";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { PageShell } from "@/components/layout/PageShell";
import { PageHeader } from "@/components/ui/page-header";
import { useT } from "@/i18n/useLocale";

type ViewFilter = "all" | "watchlist" | "gainers" | "losers" | "top10";

const FILTERS: { value: ViewFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "watchlist", label: "Watchlist" },
  { value: "gainers", label: "Gainers" },
  { value: "losers", label: "Losers" },
  { value: "top10", label: "Top 10" },
];

const Tokens = () => {
  const t = useT();
  const { user } = useAuth();
  const { tokens, loading, pricePulse, lastUpdate } = useLiveMarkets({ perPage: 100, intervalMs: 5000 });

  const [watchlist, setWatchlist] = useState<string[]>([]);
  const [filter, setFilter] = useState<ViewFilter>("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!user?.id) return;
    cryptoDataService.getUserWatchlist(user.id).then(w => setWatchlist(w.map(i => i.token_id).filter((id): id is string => !!id)));
  }, [user?.id]);

  const handleToggleWatch = async (id: string) => {
    if (!user?.id) {
      toast.error("Please sign in to manage your watchlist");
      return;
    }
    const isOn = watchlist.includes(id);
    const ok = isOn
      ? await cryptoDataService.removeFromWatchlist(user.id, id)
      : await cryptoDataService.addToWatchlist(user.id, id);
    if (!ok) return toast.error("Watchlist update failed");
    setWatchlist(prev => (isOn ? prev.filter(x => x !== id) : [...prev, id]));
    toast.success(isOn ? "Removed from watchlist" : "Added to watchlist");
  };

  const filtered = useMemo(() => {
    let list = tokens;
    switch (filter) {
      case "watchlist":
        list = list.filter(t => watchlist.includes(t.id));
        break;
      case "gainers":
        list = list.filter(t => (t.price_change_percentage_24h_in_currency ?? 0) > 0);
        break;
      case "losers":
        list = list.filter(t => (t.price_change_percentage_24h_in_currency ?? 0) < 0);
        break;
      case "top10":
        list = list.filter(t => (t.market_cap_rank ?? 999) <= 10);
        break;
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(t => t.name.toLowerCase().includes(q) || t.symbol.toLowerCase().includes(q));
    }
    return list;
  }, [tokens, filter, watchlist, search]);

  return (
    <PageShell>
      <PageHeader
        eyebrow={t("auto.markets-52c3")}
        title={t("auto.markets-52c3")}
        description={t("auto.live-crypto-prices-tech-equities-top-mov-a4b1")}
      />

      <MarketPulsePanel />




      <MarketStatsBanner />

      <HighlightsBento tokens={tokens} />


      {/* Search + chip rail */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3 mb-4">
        <div className="relative md:w-72">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t("auto.search-name-or-symbol-b00d")}
            className="pl-10 h-10 rounded-full bg-muted/40 border-border/50 shadow-none focus-visible:ring-2 focus-visible:ring-primary/30"
          />
        </div>

        <div className="flex items-center gap-2 overflow-x-auto scrollbar-none flex-1" style={{ scrollbarWidth: "none" }}>
          {FILTERS.map(f => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={cn(
                "shrink-0 h-9 px-4 rounded-full text-sm font-medium transition-colors border",
                filter === f.value
                  ? "bg-primary text-primary-foreground border-primary shadow-xs"
                  : "bg-muted/40 text-muted-foreground border-transparent hover:bg-muted hover:text-foreground",
              )}
            >
              {f.label}
            </button>
          ))}
        </div>

        {lastUpdate && (
          <span className="hidden md:inline text-xs text-muted-foreground whitespace-nowrap">
            Updated {lastUpdate.toLocaleTimeString()}
          </span>
        )}
      </div>

      <MarketsTable
        tokens={filtered}
        pricePulse={pricePulse}
        watchlist={watchlist}
        onToggleWatch={handleToggleWatch}
        loading={loading}
      />
    </PageShell>
  );
};

export default Tokens;
